import classnames from 'classnames';
import { RecordingRequestObjectSort, RecordingRequestObjectTimeLine, } from 'constants/recordingsConstants';
import { OwnPropsInterface } from 'containers/Recordings/RecordingsToolbarContainer';
import Search from 'containers/SearchCloudMeetings/SearchCloudMeetingsContainer';
import React, { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import { Button, Dropdown } from 'semantic-ui-react';
import _get from 'lodash/get';
import * as styles from './RecordingsToolbar.scss';
import Icon from 'components/Common/Icon';
import { RecordingRequestObjectFeed } from 'constants/recordingsConstants';

export interface Props {
  canShareMeetingRecordings: boolean;
  totalCount: number;
  pageNum: number;
  limit: number;
  isLoading: boolean;
  handleSearchSelect: (value: string) => void;
  handleSearchCleared: () => void;
  handleSortChange: (sortType: string) => void;
  handleDateFilterChange: (dateType: string) => void;
  initialSearchId?: string;
  canUserModifyRecording: boolean;
  openMeetingShareModal: (payload: object) => void;
  meetingId: string;
  meetingName: string;
  canUpload: boolean;
  uploadStatus: JSX.Element | null;
  openUpload: () => void;
}
const RecordingsToolbar = (props: Props & OwnPropsInterface) => {
  const [searchText, setSearchText] = useState<string|null>(null);
  const [searchId, setSearchId] = useState<string|null>(_get(props, 'initialSearchId', null));
  const { initialSearchId, meetingId, meetingName, isLoading, pageType, canUpload, uploadStatus, openUpload } = props;
  useEffect(
    () => {
      if (searchId) {
        props.handleSearchSelect(searchId);
      } else {
        props.handleSearchCleared();
      }
    },
    [searchId]
  );

  const sortOptions = [
    {
      text: intl.get('date'),
      value: RecordingRequestObjectSort.SORT_DATE,
    },
    {
      text: intl.get('views'),
      value: RecordingRequestObjectSort.SORT_VIEWS,
    }
  ];

  const filterOptions = [
    {
      text: intl.get('all'),
      value: RecordingRequestObjectTimeLine.TIMELINE_ANY,
    },
    {
      text: intl.get('today'),
      value: RecordingRequestObjectTimeLine.TIMELINE_DAY,
    },
    {
      text: intl.get('week'),
      value: RecordingRequestObjectTimeLine.TIMELINE_WEEK,
    },
    {
      text: intl.get('month'),
      value: RecordingRequestObjectTimeLine.TIMELINE_MONTH,
    },
  ];

  if (searchId && searchId === initialSearchId && isLoading && !searchText) {
    // Wait until we found the meeting to render the search input
    return (
      <div className={classnames(styles.container, 'toolbar')} />
    );
  }

  return (
    <div className={classnames(styles.container, 'toolbar')}>
      <div>
        <Search
          onSearchSelectionMade={(id: string, title: string) => {
            setSearchText(title);
            setSearchId(id);
          }}
          onSearchCleared={() => {
            setSearchText(null);
            setSearchId(null);
          }}
          placeholderText={intl.get('searchPlaceholder')}
          noResultsText={intl.get('noResults')}
          defaultMeetingText={intl.get('defaultMeeting')}
          initialSelectionId={meetingId}
          initialSelectionText={meetingName}
          showResetButton={true}
          clearOnFocus={true}
        />
        {props.canShareMeetingRecordings && pageType === RecordingRequestObjectFeed.FEED_ALL && (
          <div>
            <button
              className={`${styles.iconButton} iconButton`}
              disabled={!searchId || !props.canUserModifyRecording}
              name="btnShare"
              onClick={() => {
                // open share modal
                props.openMeetingShareModal({
                  meetingId: searchId,
                  meetingName: searchText
                });
              }}
              role="button"
              type="button"
            >
              <Icon classes={['icon-share-alt']} />
            </button>
          </div>
        )}
      </div>

      <div>
        <div className={styles.inputGroup}>
          <label>{`${intl.get('inputLabelSort')}:`}</label>
          <Dropdown
            data-test="sortDropdown"
            className={styles.input}
            closeOnBlur={true}
            defaultValue={sortOptions[0].value}
            onChange={(e, args) => {
              props.handleSortChange(args.value as string);
            }}
            options={sortOptions}
            selection={true}
            selectOnBlur={false}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>{`${intl.get('inputLabelDate')}:`}</label>
          <Dropdown
            data-test="dateDropdown"
            className={styles.input}
            closeOnBlur={true}
            defaultValue={filterOptions[0].value}
            onChange={(e, args) => {
              props.handleDateFilterChange(args.value as string);
            }}
            options={filterOptions}
            selection={true}
            selectOnBlur={false}
          />
        </div>

        {canUpload && (
          <div className={styles.buttonContainer}>
            <Button
              id="uploadRecording"
              name="uploadRecording"
              onClick={openUpload}
              role="button"
              type="button"
            >
              {intl.get('upload')}
            </Button>
            {uploadStatus}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingsToolbar;
